import { Box } from '@unifyapps/ui/components/Box';
import { Stack } from '@unifyapps/ui/components/Stack';
import { Typography } from '@unifyapps/ui/components/Typography';
import { slowCn } from '@unifyapps/ui/lib/utils';
import { clsx } from 'clsx';
import { memo } from 'react';
import { Link } from '../../../navigation';
import { DATA_NAV_ITEM_ATTRIBUTE } from '../constants';
import {
  groupNavItemLinkLabelVariants,
  groupNavItemLinkVariants,
  navIconContainerVariants,
  navItemLinkIconVariants,
} from '../styles';
import { NavigationItemSelectionVariantEnum, type NavigationItemSlotPropsType } from '../types';
import { NavigationIcon } from './NavigationIcon';

interface NavSubItemProps {
  // id of the underlying nav item. Optional: some callers (e.g. the Settings changeset picker)
  // render this component for domain objects that have no nav item id.
  id?: string;
  isActive?: boolean;
  url?: string;
  replace?: boolean;
  icon?: string;
  name: string;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  endNode?: React.ReactNode;
  variant: 'lite' | 'rich';
  slotProps?: NavigationItemSlotPropsType;
  endDecorator?: React.ReactNode;
  // optional data-* bag forwarded onto the rendered root (link/button) for test automation
  dataAttributes?: Record<`data-${string}`, string | undefined>;
}

const DEFAULT_SLOTPROPS = {
  navigationItem: {
    selectionVariant: NavigationItemSelectionVariantEnum.Stroke,
  },
};

function NavigationSubItem(props: NavSubItemProps) {
  const {
    id,
    isActive = false,
    url,
    replace,
    name,
    onClick,
    endNode,
    icon,
    variant,
    slotProps = DEFAULT_SLOTPROPS,
    endDecorator,
    dataAttributes,
  } = props;

  const {
    expanded = false,
    labelVariant,
    position,
    resolution,
    selectionVariant = NavigationItemSelectionVariantEnum.Stroke,
    typographyClassName,
  } = slotProps.navigationItem as NavigationItemSlotPropsType['navigationItem'];

  // slowCn so twMerge drops the shared base's pe-lg/py-md, which the lite branch overrides but cva
  // would otherwise leave stacked in the class string
  const itemLinkClassName = slowCn(
    groupNavItemLinkVariants({
      active: isActive,
      className: clsx(url || onClick ? '' : 'opacity-50', {
        'pe-md': endNode,
      }),
      variant,
      resolution,
      selectionVariant,
      labelVariant,
      position,
      expanded,
    }),
  );

  const navIconContainerClassName = navIconContainerVariants({
    selectionVariant,
    active: isActive,
    variant,
    resolution,
    isSubNavItem: true,
  });

  const itemLabelClassname = groupNavItemLinkLabelVariants({
    active: isActive,
    expanded,
    selectionVariant,
    labelVariant,
    variant,
  });
  const iconClassname = navItemLinkIconVariants({
    active: isActive,
    className: 'shrink-0',
    variant,
    selectionVariant,
    isSubNavItem: true,
  });

  const iconSpacer = variant === 'lite' ? null : <Box className={iconClassname} />;

  const content = (
    <Stack alignItems="center" className="w-full" direction="row" justifyContent="space-between">
      <Stack alignItems="center" className="w-full gap-x-md" direction="row">
        {icon ? (
          <Box className={navIconContainerClassName}>
            <NavigationIcon
              className={iconClassname}
              iconName={icon}
              isActive={isActive}
              label={name}
              selectionVariant={selectionVariant}
            />
          </Box>
        ) : (
          iconSpacer
        )}
        <Typography className={slowCn(itemLabelClassname, typographyClassName)}>{name}</Typography>
        {endNode}
      </Stack>
      {endDecorator}
    </Stack>
  );

  if (url) {
    return (
      <Link
        className={itemLinkClassName}
        href={url}
        onClick={onClick}
        replace={replace}
        {...dataAttributes}
        {...{ [DATA_NAV_ITEM_ATTRIBUTE]: id }}
      >
        {content}
      </Link>
    );
  }

  return (
    <Box
      as="button"
      className={itemLinkClassName}
      onClick={onClick}
      {...dataAttributes}
      {...{ [DATA_NAV_ITEM_ATTRIBUTE]: id }}
    >
      {content}
    </Box>
  );
}

export default memo(NavigationSubItem);
